import { Box, BoxProps } from "@chakra-ui/react";
import { ReactNode, memo, useMemo } from "react";
import { getBorderClassName } from "../../utils/calendar/calendarBlockUtils";
import { BlockType } from "../../types/block/type";
import { CalendarDefaultStyle } from "../../types/userCalendar/enum";
import { useFetchUserCalendarsQuery } from "../../react-query/userCalendars/queries";

interface CalendarBlockBackgroundWithBorderProps extends BoxProps {
  backgroundColor?: string;
  backgroundImage?: string;
  containerSize?: "smallEvent" | "mediumEvent" | "largeEvent";
  borderColor?: string;
  borderStyle?: string;
  borderWidth?: string;
  isTaskDone?: boolean;
  isEventDone?: boolean;
  isNotGoing?: boolean;
  isNotDoneTask?: boolean;
  isMeetWith?: boolean;
  isPendingRsvp?: boolean;
  isShowMeetWith: boolean;
  children: ReactNode;
  event: BlockType;
}

const CalendarBlockBackgroundWithBorderComponent = ({
  backgroundColor,
  backgroundImage,
  containerSize,
  borderColor,
  borderStyle,
  borderWidth,
  borderLeft,
  isTaskDone = false,
  isEventDone = false,
  isNotGoing = false,
  isNotDoneTask = false,
  isMeetWith = false,
  isPendingRsvp = false,
  isShowMeetWith,
  children,
  event,
  ...props
}: CalendarBlockBackgroundWithBorderProps) => {
  const fetchUserCalendarsQuery = useFetchUserCalendarsQuery();

  const currentCalendar = useMemo(() => {
    // 캘린더 ID가 없으면 primaryCalendar 색상 적용
    if (!event.calendarId) {
      return fetchUserCalendarsQuery.data?.find((calendar) => calendar.primary);
    }
    // calendarId로 캘린더 색상 조회해오기
    return fetchUserCalendarsQuery.data?.find((calendar) => calendar.id === event.calendarId);
  }, [fetchUserCalendarsQuery.data, event.calendarId]);

  const isShowBorder = isNotDoneTask || isMeetWith || isPendingRsvp;
  const getCalendarBackground = useMemo(() => {
    const isShowCalendarColor = !isShowMeetWith && Boolean(currentCalendar?.backgroundColor);
    const calendarColor = isShowCalendarColor
      ? currentCalendar?.backgroundColor
      : CalendarDefaultStyle.backgroundColor;

    if (isTaskDone || isEventDone) {
      return `linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), ${calendarColor}`;
    }
    return calendarColor;
  }, [currentCalendar, isEventDone, isTaskDone, isShowMeetWith]);

  return (
    <Box
      className="wrap"
      height="100%"
      display="flex"
      flexDirection="row"
      flexWrap="nowrap"
      justifyContent="space-between"
      gap="4px"
      boxSizing="border-box"
      borderRadius="5px"
      overflow="hidden"
      position="relative"
      backgroundColor={backgroundColor}
      backgroundImage={backgroundImage}
      padding={containerSize === "smallEvent" ? "0.5px 6px" : "4px 6px"}
      alignItems={containerSize === "smallEvent" ? "center" : "flex-start"}
      lineHeight={containerSize === "smallEvent" ? "1.38" : "1.2"}
      sx={{
        container: "eventWrapper / inline-size",
      }}
      {...props}
    >
      {/* 좌측 캘린더 컬러 엘리먼트 */}
      {!isMeetWith && (
        <Box
          position="absolute"
          top="0"
          left="0"
          height="100%"
          width="2px"
          background={getCalendarBackground}
        />
      )}

      {/* 통합된 border 엘리먼트 */}
      {isShowBorder && (
        <Box
          className={getBorderClassName({ isNotDoneTask })}
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          borderRadius="5px"
          border="1px solid transparent"
          borderColor={borderColor}
          borderStyle={borderStyle}
          borderWidth={borderWidth}
          borderLeft={borderLeft}
          display="block"
          sx={{
            "&.notDoneTaskBorder": {
              animation: "reminder 0.8s infinite alternate",
            },
          }}
        />
      )}

      {children}
    </Box>
  );
};

export const CalendarBlockBackgroundWithBorder = memo(
  CalendarBlockBackgroundWithBorderComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.backgroundColor === nextProps.backgroundColor &&
      prevProps.backgroundImage === nextProps.backgroundImage &&
      prevProps.containerSize === nextProps.containerSize &&
      prevProps.borderColor === nextProps.borderColor &&
      prevProps.borderStyle === nextProps.borderStyle &&
      prevProps.borderWidth === nextProps.borderWidth &&
      prevProps.borderLeft === nextProps.borderLeft &&
      prevProps.isTaskDone === nextProps.isTaskDone &&
      prevProps.isEventDone === nextProps.isEventDone &&
      prevProps.isNotGoing === nextProps.isNotGoing &&
      prevProps.isNotDoneTask === nextProps.isNotDoneTask &&
      prevProps.isMeetWith === nextProps.isMeetWith &&
      prevProps.isPendingRsvp === nextProps.isPendingRsvp &&
      prevProps.isShowMeetWith === nextProps.isShowMeetWith &&
      prevProps.event === nextProps.event
    );
  }
);
